import {AccessLevel, BigId, ColonistMembershipType, LobbyImage} from '@colonist/types'
import {UserItems} from './store/UserItems'
import {TranslationObject} from '@colonist/i18n'
import {PlayerColor} from '@colonist/game-states'
import {UserGiftedMembershipItem, UserStateData, UserStoreItem} from './UserStateData'
import {ShuffleQueueUserState} from './shuffle_queue/ShuffleQueueUserState'
import {
    checkValueIsStringBetween,
    isValueArray,
    isValueBoolean,
    isValueDate,
    isValueNumber,
    isValueNumericEnum,
    isValuePlainObject,
    isValueString,
    isValueStringBetween,
} from '@colonist/validation'
import {TimeUtils} from '@colonist/utils'
import toDate = TimeUtils.toDate
import {PaymentMethod} from './store/BaseColonistStoreProduct'

export const DELETED_USER_USERNAME = 'Deleted User'
// when adding new properties to the user state, add validation with a sensible default value
export class UserState {
    readonly id: BigId
    readonly username: string
    readonly language: string | undefined
    readonly isLoggedIn: boolean
    readonly interactedWithSite: boolean
    readonly icon: LobbyImage
    readonly items: UserItems
    readonly accessLevel: AccessLevel
    readonly vliHash: string | undefined

    // Karma Data
    readonly karma: number
    readonly karmaCompletedGameCount: number

    // Settings
    readonly preferredColor?: PlayerColor | undefined
    readonly profilePictureUrl?: string | undefined

    readonly usernameChangeAttemptsLeft: number

    readonly totalCompletedGameCount: number
    readonly ckTotalGameCount: number
    readonly ckFreeGameUntil: Date | undefined
    readonly ckNextRerollAt: Date | undefined
    readonly shuffleQueueState: ShuffleQueueUserState
    readonly regionUpdated: Date | undefined
    readonly forceSubscription: boolean

    // Discord
    readonly hasJoinedColonistDiscordServer: boolean

    constructor(source: UserStateData) {
        this.id = checkValueIsStringBetween(source.id, 1, 100)
        this.username = checkValueIsStringBetween(source.username, 1, 100)
        this.language = validateOrDefault(isValueStringBetween, undefined, source.language, 1, 5)
        this.isLoggedIn = validateOrDefault(isValueBoolean, false, source.isLoggedIn)
        this.interactedWithSite = validateOrDefault(isValueBoolean, false, source.interactedWithSite)
        this.icon = validateOrDefault(isValueNumericEnum, LobbyImage.Players, source.icon, LobbyImage)
        this.profilePictureUrl = validateOrDefault(isValueString, undefined, source.profilePictureUrl)
        this.karma = validateOrDefault(isValueNumber, 0, source.karma)
        this.karmaCompletedGameCount = validateOrDefault(isValueNumber, 0, source.karmaCompletedGameCount)
        this.preferredColor = validateOrDefault(isValueNumericEnum, undefined, source.preferredColor, PlayerColor)
        this.usernameChangeAttemptsLeft = validateOrDefault(isValueNumber, 0, source.usernameChangeAttemptsLeft)
        this.totalCompletedGameCount = validateOrDefault(isValueNumber, 0, source.totalCompletedGameCount)
        this.ckTotalGameCount = validateOrDefault(isValueNumber, 0, source.ckTotalGameCount)
        this.ckFreeGameUntil = isValueDate(source.ckFreeGameUntil) ? toDate(source.ckFreeGameUntil) : undefined
        this.ckNextRerollAt = isValueDate(source.ckNextRerollAt) ? toDate(source.ckNextRerollAt) : undefined
        this.shuffleQueueState = validateOrDefault(isValuePlainObject, undefined, source.shuffleQueueState) as ShuffleQueueUserState
        this.hasJoinedColonistDiscordServer = validateOrDefault(isValueBoolean, false, source.hasJoinedColonistDiscordServer)
        this.forceSubscription = validateOrDefault(isValueBoolean, true, source.forceSubscription)
        this.accessLevel = validateOrDefault(isValueNumericEnum, AccessLevel.User, source.accessLevel, AccessLevel)
        this.regionUpdated = isValueDate(source.regionUpdated) ? toDate(source.regionUpdated) : undefined
        this.vliHash = validateOrDefault(isValueString, undefined, source.vliHash)

        this.items = new UserItems()
        this.items.colonistCoins = validateOrDefault(isValueNumber, 0, source.colonistCoins)
        this.items.membership = validateOrDefault(isValueNumericEnum, undefined, source.membership, ColonistMembershipType)
        this.items.membershipEndDate = isValueDate(source.membershipEndDate) ? toDate(source.membershipEndDate) : undefined
        this.items.membershipPaymentMethod = validateOrDefault(isValueNumericEnum, undefined, source.membershipPaymentMethod, PaymentMethod)
        this.items.membershipPending = validateOrDefault(isValueBoolean, false, source.membershipPending)
        this.items.giftedMemberships = (validateOrDefault(isValueArray, [], source.giftedMemberships) as UserGiftedMembershipItem[])
            .filter((giftedMembership) => isValuePlainObject(giftedMembership))
            .filter((giftedMembership) => isValueNumericEnum(giftedMembership.membershipType, ColonistMembershipType))
            .filter((giftedMembership) => isValueDate(giftedMembership.giftedAt))
            .filter((giftedMembership) => isValueNumber(giftedMembership.periods))
            .map((giftedMembership) => ({
                id: giftedMembership.id,
                membershipType: giftedMembership.membershipType,
                giftedAt: toDate(giftedMembership.giftedAt),
                periods: giftedMembership.periods,
            }))

        for(const ownedItem of (validateOrDefault(isValueArray, [], source.ownedItems) as UserStoreItem[])) {
            if(!isValuePlainObject(ownedItem)) continue
            this.items.addStoreItem(ownedItem.category, ownedItem.type) // values are validated inside
        }
    }

    getPlayerKarmaStatusText(): TranslationObject {
        return {
            key: 'strings:homePage.header.playerKarma',
            options: {
                totalCompleted: this.karma,
                totalGames: this.karmaCompletedGameCount,
            },
        }
    }

    isBeginner(): boolean {
        return this.totalCompletedGameCount < 2
    }

    hasZeroCompletedGames(): boolean {
        return this.totalCompletedGameCount == 0
    }
}

type ShiftTuple<T extends any[]> =
    T extends [T[0], ...infer R] ? R : never

function validateOrDefault<T extends(...args: any) => boolean, VT>(validatorFn: T, _default: VT, value: VT, ...args: ShiftTuple<Parameters<T>>): VT {
    if(validatorFn(value, ...args)) {
        return value
    }

    return _default
}
